import React, { useEffect, useState } from "react";
import { Box, Button, Grid } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ResponsiveModal } from "@answersnow/pkg-theme";
import LabeledTextArea from "../../Widgets/LabeledTextArea";
import LabeledSelect from "../../Widgets/LabeledSelect";
import LabeledTimePicker from "../../Widgets/LabeledTimePicker";
import LabeledDatePicker from "../../Widgets/LabeledDatePicker";

const FACILITY_TYPE_OPTIONS = [
  { value: "SCHOOL", label: "School" },
  { value: "DAYCARE", label: "Daycare" },
  { value: "HOMESCHOOL", label: "Homeschool" },
  { value: "VIRTUAL_SCHOOL", label: "Virtual school" },
  { value: "NOT_YET_IN_SCHOOL_DAYCARE", label: "Not yet in school/daycare" },
];

const EDUCATIONAL_PLACEMENT_OPTIONS = [
  { value: "FULLY_INTEGRATED", label: "Fully integrated" },
  { value: "PARTIALLY_INTEGRATED", label: "Partially integrated" },
  { value: "SELF_CONTAINED_NO_SUPPORT", label: "Self-contained (no support)" },
  { value: "SELF_CONTAINED_WITH_1_1_SUPPORT", label: "Self-contained (1:1 support)" },
  { value: "HOMESCHOOL", label: "Homeschool" },
  { value: "NOT_YET_IN_DAYCARE_OR_SCHOOL", label: "Not yet in daycare or school" },
];

const DAYS_OF_WEEK_OPTIONS = [
  { value: "MONDAY", label: "Monday" },
  { value: "TUESDAY", label: "Tuesday" },
  { value: "WEDNESDAY", label: "Wednesday" },
  { value: "THURSDAY", label: "Thursday" },
  { value: "FRIDAY", label: "Friday" },
  { value: "SATURDAY", label: "Saturday" },
  { value: "SUNDAY", label: "Sunday" },
];

// Formats a Date into "HH:mm"
const formatTimeToString = (date: Date | null): string | null => {
  if (!date) return null;
  const h = String(date.getHours()).padStart(2, "0");
  const m = String(date.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
};

export interface EducationModalFormValues {
  schoolName: string;
  teacherName: string;
  grade: string;
  facilityType: string;
  educationalPlacement: string;
  daysOfWeek: string[];
  startTime: Date | null;
  endTime: Date | null;
  startDate: Date | null;
  endDate: Date | null;
}

const INITIAL_FORM: EducationModalFormValues = {
  schoolName: "",
  teacherName: "",
  grade: "",
  facilityType: "",
  educationalPlacement: "",
  daysOfWeek: [],
  startTime: null,
  endTime: null,
  startDate: null,
  endDate: null,
};

export interface EducationModalSaveValues {
  schoolName: string;
  teacherName: string;
  grade: string;
  facilityType: string;
  educationalPlacement: string;
  daysOfWeek: string[];
  startTime: string | null;
  endTime: string | null;
  startDate: Date | null;
  endDate: Date | null;
}

interface EducationModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (values: EducationModalSaveValues) => void;
  isSaving?: boolean;
  initialValues?: Partial<EducationModalFormValues>;
}

export const EducationModal: React.FC<EducationModalProps> = ({
  open,
  onClose,
  onSave,
  isSaving = false,
  initialValues,
}) => {
  const [formValues, setFormValues] = useState<EducationModalFormValues>({
    ...INITIAL_FORM,
    ...initialValues,
  });

  useEffect(() => {
    if (open) {
      setFormValues({ ...INITIAL_FORM, ...initialValues });
    }
  }, [open]);

  const handleChange =
    (field: keyof EducationModalFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleTimeChange = (field: "startTime" | "endTime") => (value: Date | null) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (field: "startDate" | "endDate") => (value: Date | null) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    if (onSave) {
      onSave({
        ...formValues,
        startTime: formatTimeToString(formValues.startTime),
        endTime: formatTimeToString(formValues.endTime),
      });
    } else {
      handleClose();
    }
  };

  const isEditMode = Boolean(initialValues && Object.keys(initialValues).length > 0);

  return (
    <ResponsiveModal
      open={open}
      onClose={handleClose}
      title={isEditMode ? "Edit School" : "Add School"}
      maxWidth="xs"
      drawerContentSx={{ maxHeight: "calc(80vh - 160px)" }}
    >
      <Box sx={{ p: 0 }}>
        <Grid container spacing={7} sx={{ p: "24px 0px" }}>
          <LabeledTextArea
            label="School Name"
            value={formValues.schoolName}
            onChange={handleChange("schoolName")}
            multiline={false}
          />
          <LabeledTextArea
            label="Teacher's Name"
            value={formValues.teacherName}
            onChange={handleChange("teacherName")}
            multiline={false}
          />
          <LabeledTextArea
            label="Grade"
            value={formValues.grade}
            onChange={handleChange("grade")}
            multiline={false}
          />
          <LabeledDatePicker
            label="Start Date"
            value={formValues.startDate}
            onChange={handleDateChange("startDate")}
          />
          <LabeledDatePicker
            label="End Date"
            value={formValues.endDate}
            onChange={handleDateChange("endDate")}
          />
          <LabeledSelect
            label="Facility Type"
            value={formValues.facilityType}
            onChange={handleChange("facilityType")}
            options={FACILITY_TYPE_OPTIONS}
          />
          <LabeledSelect
            label="Educational Placement"
            value={formValues.educationalPlacement}
            onChange={handleChange("educationalPlacement")}
            options={EDUCATIONAL_PLACEMENT_OPTIONS}
          />
          <LabeledSelect
            label="Days of Week"
            multiple
            value={formValues.daysOfWeek}
            onChange={(val) => setFormValues((prev) => ({ ...prev, daysOfWeek: val }))}
            options={DAYS_OF_WEEK_OPTIONS}
          />
          <LabeledTimePicker
            label="Start Time"
            value={formValues.startTime}
            onChange={handleTimeChange("startTime")}
          />
          <LabeledTimePicker
            label="End Time"
            value={formValues.endTime}
            onChange={handleTimeChange("endTime")}
          />
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3, mt: "16px" }}>
          <Button variant="contained" color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton variant="contained" size="small" onClick={handleSave} loading={isSaving}>
            {isEditMode ? "Save Changes" : "Add School"}
          </LoadingButton>
        </Box>
      </Box>
    </ResponsiveModal>
  );
};

export default EducationModal;
